import { AlertCircle, Landmark, RefreshCcw } from "lucide-react";
import { useMemo } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAmountDisplay } from "@/shared/components/data-display/ColocaAmountDisplay";
import {
  ColocaDataTable,
  type ColocaDataTableColumn,
} from "@/shared/components/data-display/ColocaDataTable";
import { ColocaKpiCard } from "@/shared/components/data-display/ColocaKpiCard";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaProgress } from "@/shared/components/feedback/ColocaProgress";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { NoAccessView } from "@/shared/components/NoAccessView";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatDateTime, formatTimeAgo } from "@/shared/utils/dateTimeHelpers";
import { compareDecimalStrings } from "@/shared/utils/decimal";
import { formatRail } from "@/shared/utils/railDisplay";

import type {
  ProviderFloat,
  ProviderFloatsOverview,
  ProviderFloatStatus,
  RuntimeWalletCurrencySummary,
} from "../../types/payments-wallets.types";
import {
  coverageRows,
  groupFloatsByProvider,
  providerName,
  type CoverageRow,
} from "../../utils/providerFloats";
import { overviewFractionDigits } from "../../utils/walletsOverview";
import { CurrencyMark } from "./CurrencyMark";

const STATUS_VARIANT: Record<ProviderFloatStatus, "success" | "warning" | "danger" | "neutral"> = {
  OK: "success",
  STALE: "warning",
  UNAVAILABLE: "danger",
  UNSUPPORTED: "neutral",
};

export interface ProviderFloatsPanelProps {
  data: ProviderFloatsOverview | null;
  isLoading: boolean;
  error: string | null;
  hasAccess: boolean;
  /** The currencies of the wallets summary, for the coverage. */
  currencies: RuntimeWalletCurrencySummary[];
  masked: boolean;
  /** Omitted without `payments:write`: the button is not offered. */
  onReadAgain?: () => void;
  refreshDenied: boolean;
}

/**
 * INTERNAL staff treasury: what Coloca must back in each currency against what each provider reports holding,
 * then every float by provider. Named by provider on purpose — the staff-only exception to rule 12.
 */
export function ProviderFloatsPanel({
  data,
  isLoading,
  error,
  hasAccess,
  currencies,
  masked,
  onReadAgain,
  refreshDenied,
}: ProviderFloatsPanelProps) {
  const { t, activeLanguage } = useTranslations("wallets");
  const groups = useMemo(() => groupFloatsByProvider(data?.floats ?? []), [data]);
  const coverage = useMemo(() => coverageRows(currencies, data?.floats ?? []), [currencies, data]);

  if (!hasAccess) return <NoAccessView />;

  const updatedAt = data
    ? formatDateTime(data.generated_at, { locale: activeLanguage, includeTime: true })
    : null;

  return (
    <div
      className={cn("space-y-6 transition-opacity", isLoading && data && "opacity-60")}
      aria-busy={isLoading}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-muted-foreground">
          {updatedAt ? t("overview_updated", { time: updatedAt }) : null}
        </p>
        {onReadAgain && (
          <ColocaButton
            variant="secondary"
            size="sm"
            icon={RefreshCcw}
            onClick={onReadAgain}
            disabled={isLoading}
            title={t("providers_refresh_hint")}
          >
            {t("providers_refresh")}
          </ColocaButton>
        )}
      </div>

      {refreshDenied && (
        <ColocaAlert variant="warning" icon={AlertCircle} text={t("providers_refresh_denied")} />
      )}
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={t("providers_error")} />}

      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">{t("coverage_title")}</h2>
          <p className="text-sm text-muted-foreground">{t("coverage_hint")}</p>
        </div>
        {isLoading && !data ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {Array.from({ length: 2 }, (_, index) => (
              <ColocaSkeleton key={index} className="h-40 rounded-xl" />
            ))}
          </div>
        ) : coverage.length === 0 ? (
          <ColocaEmpty surface="card" icon={Landmark} title={t("coverage_nothing_to_back")} />
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {coverage.map((row) => (
              <CoverageCard key={row.currency} row={row} masked={masked} />
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div>
          <h2 className="text-base font-semibold text-foreground">{t("providers_title")}</h2>
          <p className="text-sm text-muted-foreground">{t("providers_footnote")}</p>
        </div>
        {isLoading && !data ? (
          <ColocaSkeleton className="h-48 rounded-xl" />
        ) : groups.length === 0 ? (
          <ColocaEmpty surface="card" icon={Landmark} title={t("providers_empty")} />
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <ProviderSection
                key={group.providerCode}
                providerCode={group.providerCode}
                floats={group.floats}
                totals={group.totals}
                masked={masked}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function CoverageCard({ row, masked }: { row: CoverageRow; masked: boolean }) {
  const { t, activeLanguage } = useTranslations("wallets");
  const digits = overviewFractionDigits(row.currency);
  const amount = (value: string) => (
    <ColocaAmountDisplay
      value={value}
      fractionDigits={digits}
      masked={masked}
      size="xs"
      className="text-foreground"
    />
  );
  const percent = (value: number) =>
    new Intl.NumberFormat(activeLanguage, { style: "percent", maximumFractionDigits: 0 }).format(value);
  const short = row.difference !== null && compareDecimalStrings(row.difference, "0") < 0;
  const gap = row.difference?.startsWith("-") ? row.difference.slice(1) : row.difference;

  const value =
    row.inProviders === null
      ? t("coverage_no_reading")
      : row.ratio === null
        ? t("coverage_nothing_to_back")
        : percent(row.ratio);

  return (
    <ColocaKpiCard
      label={
        <span className="flex items-center gap-2">
          <CurrencyMark currency={row.currency} />
          <span aria-hidden="true">·</span>
          <span>{t("coverage_label")}</span>
        </span>
      }
      value={
        <span className={cn(row.ratio !== null && (short ? "text-destructive" : "text-success"))}>
          {value}
        </span>
      }
      meta={
        <span className="flex flex-col gap-2.5">
          {row.ratio !== null && (
            <ColocaProgress
              value={Math.min(row.ratio, 1) * 100}
              size="sm"
              tone={short ? "danger" : "success"}
              aria-label={t("coverage_label")}
            />
          )}
          <span className="grid grid-cols-[auto_1fr] items-baseline gap-x-3 gap-y-1">
            <span>{t("coverage_held")}</span>
            <span className="text-right">{amount(row.held)}</span>
            <span>{t("coverage_in_providers")}</span>
            <span className="text-right">{row.inProviders === null ? "—" : amount(row.inProviders)}</span>
          </span>
          {gap && compareDecimalStrings(row.held, "0") > 0 && (
            <span className={cn("font-medium", short ? "text-destructive" : "text-success")}>
              {t(short ? "coverage_short" : "coverage_surplus")}{" "}
              <ColocaAmountDisplay
                value={gap}
                currency={row.currency}
                fractionDigits={digits}
                masked={masked}
                size="xs"
                className="text-current"
              />
            </span>
          )}
          {row.partial && <span className="text-warning">{t("coverage_partial")}</span>}
        </span>
      }
    />
  );
}

interface ProviderSectionProps {
  providerCode: string;
  floats: ProviderFloat[];
  totals: { currency: string; amount: string }[];
  masked: boolean;
}

function ProviderSection({ providerCode, floats, totals, masked }: ProviderSectionProps) {
  const { t, activeLanguage } = useTranslations("wallets");

  const columns: ColocaDataTableColumn<ProviderFloat>[] = [
    {
      id: "account",
      header: t("float_col_account"),
      headerClassName: "w-[38%]",
      cell: (float) => (
        <span className="flex flex-col gap-0.5">
          <span className="font-mono text-xs font-medium text-foreground">
            {float.account ?? float.currency}
          </span>
          <span className="text-xs text-muted-foreground">
            {float.direction === "PAYIN"
              ? t("float_use_payin", { rail: formatRail(float.rail) })
              : t("float_use_payout", { rail: formatRail(float.rail) })}{" "}
            · {float.country}
          </span>
        </span>
      ),
    },
    {
      id: "available",
      header: t("float_col_available"),
      headerClassName: "w-[22%] text-right",
      cellClassName: "text-right",
      cell: (float) =>
        float.available === null ? (
          <span className="text-xs text-muted-foreground">—</span>
        ) : (
          <ColocaAmountDisplay
            value={float.available}
            currency={float.currency}
            fractionDigits={overviewFractionDigits(float.currency)}
            masked={masked}
            size="sm"
            className="text-foreground"
          />
        ),
    },
    {
      id: "status",
      header: t("float_col_status"),
      headerClassName: "w-[22%]",
      cell: (float) => (
        <span className="flex flex-col items-start gap-0.5">
          <ColocaStatusPill size="sm" variant={STATUS_VARIANT[float.status]} withDot>
            {t(`float_status_${float.status}`)}
          </ColocaStatusPill>
          {float.error_code && float.status !== "OK" && (
            <span className="font-mono text-[10px] text-muted-foreground">{float.error_code}</span>
          )}
        </span>
      ),
    },
    {
      id: "read",
      header: t("float_col_read"),
      cell: (float) => (
        <span className="text-xs text-muted-foreground">
          {float.fetched_at ? formatTimeAgo(float.fetched_at, { locale: activeLanguage }) : "—"}
        </span>
      ),
    },
  ];

  return (
    <section className="overflow-hidden rounded-xl border border-border bg-card">
      <header className="flex flex-wrap items-baseline justify-between gap-3 border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">{providerName(providerCode)}</h3>
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          {totals.map((total) => (
            <ColocaAmountDisplay
              key={total.currency}
              value={total.amount}
              currency={total.currency}
              fractionDigits={overviewFractionDigits(total.currency)}
              masked={masked}
              size="sm"
              className="text-foreground"
            />
          ))}
        </span>
      </header>
      <ColocaDataTable
        frame="none"
        columns={columns}
        rows={floats}
        rowKey={(float) => `${float.direction}:${float.rail}:${float.country}:${float.currency}`}
      />
    </section>
  );
}
